/**
 * @file Badge.tsx
 * @description 통합 Badge 컴포넌트 (SSOT)
 *
 * 주요 기능:
 * 1. 프로젝트 특화 모드: 난이도/등급 표시 (type="difficulty" | "grade")
 * 2. 범용 모드: variant 기반 배지 (shadcn/ui 스타일)
 *
 * 핵심 구현 로직:
 * - 프로젝트 특화: 난이도별/등급별 브랜드 색상 자동 적용
 * - 범용 모드: shadcn/ui badgeVariants 사용
 * - 타입 가드로 두 모드 자동 구분
 *
 * 브랜드 통합:
 * - Design System v2.2: 브랜드 Accent Colors 사용 (프로젝트 특화 모드)
 * - Green: Easy 난이도, B 등급 (성장 Growth)
 * - Amber: Normal 난이도, C 등급 (경고/학습 시그널)
 * - Blue: A 등급 (Financial clarity 핵심)
 *
 * @dependencies
 * - @/components/shared/ui/badge: badgeVariants (범용 모드용)
 * - @radix-ui/react-slot: asChild 지원
 * - class-variance-authority: variant 타입
 * - @/lib/utils: cn 함수
 *
 * @see {@link /docs/product/current-architecture-ssot.md} - 현재 공통 UI 컴포넌트 운영 기준
 * @see {@link /docs/system/difficulty-modes.md} - 난이도 설명
 * @see {@link /docs/ui/design-system.md} - 브랜드 Accent Colors 사용 규칙
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/shared/core-utils";
import { badgeVariants } from "@/components/shared/ui/badge";

// badgeVariants에서 variant 타입 추출
type BadgeVariantType = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

// 프로젝트 특화 Badge Props
export interface ProjectBadgeProps {
  type: "difficulty" | "grade";
  value: string;
  className?: string;
  children?: never;
  variant?: never;
}

// 범용 Badge Props
export interface GenericBadgeProps
  extends Omit<React.ComponentProps<"span">, "variant">,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
  type?: never;
  value?: never;
  children?: React.ReactNode;
}

// variant prop 타입 정의 (export용)
export type BadgeVariant = BadgeVariantType;

// 통합 Badge Props (타입 가드로 구분)
export type BadgeProps = GenericBadgeProps | ProjectBadgeProps;

/**
 * 통합 Badge 컴포넌트
 *
 * 사용 예시:
 * ```tsx
 * // 프로젝트 특화 모드
 * <Badge type="difficulty" value="Easy" />
 * <Badge type="grade" value="A" />
 *
 * // 범용 모드
 * <Badge variant="default">New</Badge>
 * <Badge variant="secondary">Beta</Badge>
 * ```
 */
// 함수 오버로드로 타입 추론 개선
export function Badge(props: ProjectBadgeProps): React.ReactElement;
export function Badge(props: GenericBadgeProps): React.ReactElement;
export function Badge(
  props: ProjectBadgeProps | GenericBadgeProps
): React.ReactElement {
  // 프로젝트 특화 모드 감지 (type이 "difficulty" 또는 "grade"인 경우)
  if ("type" in props && (props.type === "difficulty" || props.type === "grade")) {
    const { type, value, className } = props as ProjectBadgeProps;

    const getColorClass = () => {
      if (type === "difficulty") {
        switch (value.toLowerCase()) {
          case "easy":
            // 브랜드 Accent Green 사용
            return "bg-[hsl(var(--accent-green))]/10 text-[hsl(var(--accent-green))] dark:bg-[hsl(var(--accent-green))]/20 dark:text-[hsl(var(--accent-green))]";
          case "normal":
            // 브랜드 Accent Amber 사용
            return "bg-[hsl(var(--accent-amber))]/10 text-[hsl(var(--accent-amber))] dark:bg-[hsl(var(--accent-amber))]/20 dark:text-[hsl(var(--accent-amber))]";
          case "hard":
            return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
          default:
            return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
        }
      }

      if (type === "grade") {
        switch (value.toUpperCase()) {
          case "S":
            return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
          case "A":
            // 브랜드 Accent Blue 사용 (Financial clarity 핵심)
            return "bg-[hsl(var(--accent-blue))]/10 text-[hsl(var(--accent-blue))] dark:bg-[hsl(var(--accent-blue))]/20 dark:text-[hsl(var(--accent-blue))]";
          case "B":
            // 브랜드 Accent Green 사용 (성장 Growth)
            return "bg-[hsl(var(--accent-green))]/10 text-[hsl(var(--accent-green))] dark:bg-[hsl(var(--accent-green))]/20 dark:text-[hsl(var(--accent-green))]";
          case "C":
            // 브랜드 Accent Amber 사용 (경고/학습 시그널)
            return "bg-[hsl(var(--accent-amber))]/10 text-[hsl(var(--accent-amber))] dark:bg-[hsl(var(--accent-amber))]/20 dark:text-[hsl(var(--accent-amber))]";
          case "D":
            return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
          default:
            return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
        }
      }
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    };

    const getAriaLabel = () => {
      if (type === "difficulty") {
        const labels: Record<string, string> = {
          easy: "쉬운 난이도",
          normal: "보통 난이도",
          hard: "어려운 난이도",
        };
        return labels[value.toLowerCase()] || `${value} 난이도`;
      }
      if (type === "grade") {
        return `${value} 등급`;
      }
      return value;
    };

    return (
      <span
        className={cn(
          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
          getColorClass(),
          className
        )}
        aria-label={getAriaLabel()}
      >
        {value}
      </span>
    );
  }

  // 범용 모드 (shadcn/ui 스타일)
  const { className, variant, asChild = false, children, ...restProps } = props as GenericBadgeProps;
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...restProps}
    >
      {children}
    </Comp>
  );
}

// badgeVariants도 export (기존 ui/badge 사용처 호환성)
export { badgeVariants } from "@/components/shared/ui/badge";
